import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play } from "lucide-react";
import { api } from "../../lib/api";
import { useBrand } from "../../contexts/BrandContext";
import { brandLink } from "../../components/PublicLayout";
import SEO from "../../components/SEO";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1762279389042-9439bfb6c155?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxkYXJrJTIwYWJzdHJhY3QlMjB0ZWNoJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3Nzg1MzQ0NjF8MA&ixlib=rb-4.1.0&q=85";

function youtubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
  return m ? m[1] : null;
}

// Bento grid pattern: cycles through varying spans for visual interest.
const BENTO_SPANS = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-2 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-2",
  "md:col-span-2 md:row-span-1",
  "md:col-span-1 md:row-span-1",
];

function CategoryBento({ categories }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 auto-rows-[200px] md:auto-rows-[180px]"
      data-testid="category-bento"
    >
      {categories.map((c, idx) => (
        <Link
          key={c.id}
          to={brandLink(`/urunler?category=${c.id}`)}
          className={`group relative block overflow-hidden border border-white/10 bg-[#0A0A0A] ${BENTO_SPANS[idx % BENTO_SPANS.length]}`}
          data-testid={`category-card-${c.slug}`}
        >
          <img
            src={c.image}
            alt={c.name}
            className="absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out group-hover:scale-110 group-hover:brightness-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
          <div className="absolute inset-0 p-6 flex flex-col justify-end">
            <div className="font-display text-2xl sm:text-3xl font-medium tracking-tight text-white drop-shadow-lg">
              {c.name}
            </div>
            {c.description && (
              <div className="text-sm text-zinc-300 mt-2 line-clamp-2 max-w-md">{c.description}</div>
            )}
            <div className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/80 group-hover:text-white">
              Keşfet <ArrowUpRight size={14} />
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Home() {
  const { mode, brand } = useBrand();
  const [homepage, setHomepage] = useState(null);
  const [brands, setBrands] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const load = async () => {
      const slug = mode === "brand" ? brand.slug : "_all";
      const [hp, brs] = await Promise.all([
        api.get(`/homepage/${slug}`).then((r) => r.data).catch(() => null),
        mode === "main" ? api.get(`/brands?active_only=true`).then((r) => r.data) : Promise.resolve([]),
      ]);
      setHomepage(hp);
      setBrands(brs);

      const catParams = new URLSearchParams();
      if (mode === "brand" && brand) catParams.set("brand_id", brand.id);
      const cats = await api.get(`/categories?${catParams.toString()}`).then((r) => r.data).catch(() => []);
      setCategories(cats.filter((c) => c.image));

      const params = new URLSearchParams();
      if (mode === "brand") params.set("brand_slug", brand.slug);
      params.set("featured", "true");
      const fp = await api.get(`/products?${params.toString()}`).then((r) => r.data).catch(() => []);
      setFeatured(fp.slice(0, 8));
    };
    load();
  }, [mode, brand]);

  const heroImg = homepage?.hero_image || FALLBACK_HERO;
  const heroTitle =
    homepage?.hero_title ||
    (mode === "brand"
      ? `${brand.name} — Türkiye Resmi Distribütörü`
      : "Profesyonel Fotoğraf Ekipmanlarının Türkiye Distribütörü");
  const heroSubtitle =
    homepage?.hero_subtitle ||
    (mode === "brand"
      ? `${brand.name} ürünleri Türkiye'de yalnızca Noon Foto güvencesiyle.`
      : "Astrhori, Wise ve daha fazla markanın yetkili distribütörü.");

  return (
    <div className="fade-in-up" data-testid="home-page">
      <SEO home />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="w-full h-full object-cover" />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.1) 100%)",
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 lg:py-44">
          <div className="max-w-3xl">
            <div
              className="inline-block text-xs uppercase tracking-[0.3em] mb-6 px-3 py-1 border"
              style={{ borderColor: "var(--brand-primary)", color: "var(--brand-primary)" }}
              data-testid="hero-badge"
            >
              {mode === "brand" ? "Resmi Distribütör" : "Noon Foto"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter leading-[1.05]">
              {heroTitle}
            </h1>
            <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-2xl leading-relaxed">{heroSubtitle}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to={brandLink("/urunler")}
                className="brand-btn inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-[0.2em]"
                data-testid="hero-cta-products"
              >
                Ürünleri Keşfet <ArrowUpRight size={16} />
              </Link>
              <Link
                to={brandLink("/bayiler")}
                className="inline-flex items-center gap-2 px-6 py-3 text-sm uppercase tracking-[0.2em] border border-white/20 hover:bg-white/5 transition-colors"
                data-testid="hero-cta-dealers"
              >
                Bayiler
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Category Showcase (bento grid) */}
      {categories.length > 0 && (
        <section className="border-b border-white/10 py-20" data-testid="category-showcase">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Kategoriler</div>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Ürün Kategorileri</h2>
              </div>
            </div>
            <CategoryBento categories={categories} />
          </div>
        </section>
      )}

      {/* Brand Showcase (main site only) */}
      {mode === "main" && brands.length > 0 && (
        <section className="border-b border-white/10 py-20" data-testid="brand-showcase">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Markalarımız</div>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Distribütörlüğünü Yaptığımız Markalar</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {brands.map((b) => (
                <Link
                  key={b.id}
                  to={`/?brand=${b.slug}`}
                  className="group block border border-white/10 bg-[#0A0A0A] hover:border-white/30 transition-all p-8"
                  data-testid={`brand-card-${b.slug}`}
                >
                  <div className="h-20 flex items-center mb-6">
                    {b.logo ? (
                      <img src={b.logo} alt={b.name} className="max-h-16 max-w-[180px] object-contain" />
                    ) : (
                      <div className="font-display text-2xl font-semibold">{b.name}</div>
                    )}
                  </div>
                  <div className="text-sm text-zinc-400 line-clamp-2 mb-6">{b.description || "—"}</div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-300 group-hover:text-white">
                    İncele <ArrowUpRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      {featured.length > 0 && (
        <section className="border-b border-white/10 py-20" data-testid="featured-products">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Öne Çıkanlar</div>
                <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Öne Çıkan Ürünler</h2>
              </div>
              <Link
                to={brandLink("/urunler")}
                className="hidden sm:flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white"
              >
                Tümü <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {featured.map((p) => (
                <Link
                  key={p.id}
                  to={brandLink(`/urunler/${p.slug}`)}
                  className="group block border border-white/10 bg-[#0A0A0A] hover:-translate-y-1 transition-all"
                  style={{ "--hover-border": "var(--brand-primary)" }}
                  data-testid={`featured-product-${p.slug}`}
                >
                  <div className="aspect-square overflow-hidden bg-[#141414] p-8">
                    {p.images?.[0] ? (
                      <img
                        src={p.images[0]}
                        alt={p.name}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700 text-xs">
                        No Image
                      </div>
                    )}
                  </div>
                  <div className="p-4 border-t border-white/10">
                    <div className="text-sm font-medium line-clamp-1">{p.name}</div>
                    <div className="text-xs text-zinc-500 mt-1 uppercase tracking-[0.15em]">
                      {p.stock_status === "in_stock" && "Stokta"}
                      {p.stock_status === "out_of_stock" && "Tükendi"}
                      {p.stock_status === "coming_soon" && "Çok Yakında"}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* YouTube Videos */}
      {homepage?.youtube_videos?.length > 0 && (
        <section className="border-b border-white/10 py-20" data-testid="youtube-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Videolar</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight">İncelemeler & Tanıtım</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {homepage.youtube_videos.map((v, idx) => {
                const id = youtubeId(v.url);
                if (!id) return null;
                return (
                  <a
                    key={idx}
                    href={v.url}
                    target="_blank"
                    rel="noreferrer"
                    className="group block border border-white/10 bg-[#0A0A0A] hover:border-white/30 transition-all"
                    data-testid={`youtube-card-${idx}`}
                  >
                    <div className="aspect-video relative overflow-hidden">
                      <img
                        src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`}
                        alt={v.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <Play size={48} className="text-white" />
                      </div>
                    </div>
                    <div className="p-4 text-sm">{v.title}</div>
                  </a>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
